/**
 * Advanced Analysis API Service
 * Handles geographic analysis, trends, and performance indicators
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1') + '/sis-integration';

// Helper to build auth headers for fetch calls
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Token ${token}` } : {}),
  };
};

export interface GeographicData {
  region_name?: string;
  district_name?: string;
  school_name?: string;
  region_code?: string;
  district_code?: string;
  school_id?: string;
  total_schools?: number;
  total_students: number;
  male_students: number;
  female_students: number;
  total_classrooms?: number;
  total_toilets?: number;
  total_libraries?: number;
  utilization_rate?: number;
  latitude?: number;
  longitude?: number;
  school_level?: string;
  capacity?: number;
}

export interface TrendData {
  academic_year: string;
  total_students: number;
  male_students: number;
  female_students: number;
  growth_rate?: number;
}

export interface TrendAnalysis {
  trends: TrendData[];
  summary: {
    total_years: number;
    avg_growth_rate: number;
  };
}

export interface PerformanceKPIs {
  total_schools: number;
  total_students: number;
  total_staff: number;
  male_students: number;
  female_students: number;
  student_teacher_ratio: number;
  female_enrollment_pct: number;
  schools_per_1000_students: number;
  gender_parity_index: number;
}

export interface ComparisonData {
  entity_name: string;
  entity_code: string;
  total_students: number;
  total_staff: number;
  total_schools: number;
  student_teacher_ratio: number;
}

export interface PredictiveInsight {
  year: number;
  predicted_enrollment: number;
  confidence: number;
}

export interface PredictiveAnalysis {
  predictions: PredictiveInsight[];
  insights: string[];
  historical_data: TrendData[];
}

export interface AnalysisData {
  status: string;
  data: any;
  level?: string;
  type?: string;
  dimension?: string;
  period?: string;
  scope?: string;
}

export interface AnalysisFilters {
  level?: string;
  type?: string;
  academic_year?: string;
  dimension?: string;
  period?: string;
  scope?: string;
  scope_code?: string;
  region?: string;
  district?: string;
  entities?: string[];
  metric?: string;
  horizon?: string;
}

/**
 * Get analysis data based on analysis type and filters
 */
export const getAnalysisData = async (
  analysisType: 'geographic' | 'trends' | 'comparison' | 'predictions' | 'kpis',
  filters: AnalysisFilters = {}
): Promise<AnalysisData> => {
  let endpoint = '';
  const params = new URLSearchParams();

  // Build endpoint and parameters based on analysis type
  switch (analysisType) {
    case 'geographic':
      endpoint = 'geographic-analysis';
      if (filters.type) params.append('type', filters.type);
      if (filters.level) params.append('level', filters.level);
      if (filters.academic_year) params.append('academic_year', filters.academic_year);
      break;

    case 'trends':
      endpoint = 'trend-analysis';
      if (filters.dimension) params.append('dimension', filters.dimension);
      if (filters.period) params.append('period', filters.period);
      if (filters.region) params.append('region', filters.region);
      if (filters.district) params.append('district', filters.district);
      break;

    case 'comparison':
      endpoint = 'comparative-analysis';
      if (filters.type) params.append('type', filters.type);
      if (filters.metric) params.append('metric', filters.metric);
      if (filters.entities) {
        filters.entities.forEach(entity => params.append('entities', entity));
      }
      break;

    case 'predictions':
      endpoint = 'predictive-insights';
      if (filters.type) params.append('type', filters.type);
      if (filters.horizon) params.append('horizon', filters.horizon);
      if (filters.region) params.append('region', filters.region);
      break;

    case 'kpis':
      endpoint = 'performance-indicators';
      if (filters.scope) params.append('scope', filters.scope);
      if (filters.scope_code) params.append('scope_code', filters.scope_code);
      break;

    default:
      throw new Error(`Unknown analysis type: ${analysisType}`);
  }

  const url = `${API_BASE_URL}/${endpoint}/${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch ${analysisType} analysis: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get geographic analysis data for map visualization
 */
export const getGeographicAnalysis = async (
  level: 'region' | 'district' | 'school' = 'region',
  analysisType: 'enrollment' | 'infrastructure' | 'performance' = 'enrollment',
  academicYear?: string
): Promise<AnalysisData> => {
  return getAnalysisData('geographic', {
    level,
    type: analysisType,
    academic_year: academicYear
  });
};

/**
 * Get trend analysis data
 */
export const getTrendAnalysis = async (
  dimension: 'enrollment' | 'staff' | 'infrastructure' = 'enrollment',
  period: '3years' | '5years' | 'annual' = '5years',
  regionCode?: string,
  districtCode?: string
): Promise<AnalysisData> => {
  return getAnalysisData('trends', {
    dimension,
    period,
    region: regionCode,
    district: districtCode
  });
};

/**
 * Get comparative analysis between entities
 */
export const getComparativeAnalysis = async (
  comparisonType: 'performance' | 'trends' = 'performance',
  entities: string[],
  metric: 'enrollment_rate' | 'student_teacher_ratio' | 'total_students' = 'total_students'
): Promise<AnalysisData> => {
  return getAnalysisData('comparison', {
    type: comparisonType,
    entities,
    metric
  });
};

/**
 * Get predictive insights
 */
export const getPredictiveInsights = async (
  insightType: 'enrollment_forecast' | 'infrastructure_needs' = 'enrollment_forecast',
  horizon: '3years' | '5years' = '3years',
  regionCode?: string
): Promise<AnalysisData> => {
  return getAnalysisData('predictions', {
    type: insightType,
    horizon,
    region: regionCode
  });
};

/**
 * Get performance indicators (KPIs)
 */
export const getPerformanceIndicators = async (
  scope: 'national' | 'regional' | 'district' = 'national',
  scopeCode?: string
): Promise<AnalysisData> => {
  return getAnalysisData('kpis', {
    scope,
    scope_code: scopeCode
  });
};

/**
 * Helper function to format numbers for display
 */
export const formatAnalysisNumber = (value: number, type: 'short' | 'full' = 'short'): string => {
  if (type === 'short') {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
  }
  return value.toLocaleString();
};

/**
 * Helper function to calculate growth rate
 */
export const calculateGrowthRate = (current: number, previous: number): number => {
  if (previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 100 * 100) / 100;
};

/**
 * Get available years from the database
 */
export const getAvailableYears = async (): Promise<number[]> => {
  const response = await fetch(`${API_BASE_URL}/available-years/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch available years: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || [];
};

/**
 * Get chart data based on configuration
 */
export const getChartData = async (config: {
  indicator: string;
  xAxis: string;
  splitBy?: string;
  year?: string;
}): Promise<any> => {
  const params = new URLSearchParams();
  params.append('indicator', config.indicator);
  params.append('xAxis', config.xAxis);
  if (config.splitBy) params.append('splitBy', config.splitBy);
  if (config.year) params.append('year', config.year);

  const response = await fetch(`${API_BASE_URL}/chart-data/?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch chart data: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get regions list for filtering
 */
export const getRegions = async (): Promise<any[]> => {
  const response = await fetch(`${API_BASE_URL}/geographic-analysis/?level=region&type=enrollment`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  return data.data || [];
};

/**
 * Get districts list for filtering (filtered by region if provided)
 */
export const getDistricts = async (regionCode?: string): Promise<any[]> => {
  const response = await fetch(`${API_BASE_URL}/geographic-analysis/?level=district&type=enrollment`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  const districts = data.data || [];
  
  // If regionCode is provided, filter districts by region_name matching the selected region
  if (regionCode) {
    // First find the region name from our regions data
    const regions = await getRegions();
    const selectedRegion = regions.find(r => r.region_code === regionCode);
    if (selectedRegion) {
      return districts.filter((district: any) => 
        district.region_name === selectedRegion.region_name
      );
    }
  }
  
  return districts;
};

/**
 * Helper function to get analysis type color
 */
export const getAnalysisTypeColor = (analysisType: string): 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error' => {
  const colorMap: Record<string, 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error'> = {
    enrollment: 'primary',
    infrastructure: 'secondary',
    performance: 'success',
    trends: 'info',
    predictions: 'warning',
    comparison: 'error'
  };
  
  return colorMap[analysisType] || 'primary';
};

/**
 * Helper function to determine if a metric is good/bad based on value
 */
export const getMetricStatus = (metric: string, value: number): 'good' | 'warning' | 'bad' => {
  switch (metric) {
    case 'student_teacher_ratio':
      if (value <= 30) return 'good';
      if (value <= 40) return 'warning';
      return 'bad';
      
    case 'gender_parity_index':
      if (value >= 0.9 && value <= 1.1) return 'good';
      if (value >= 0.8 && value <= 1.2) return 'warning';
      return 'bad';
      
    case 'utilization_rate':
      if (value >= 70 && value <= 90) return 'good';
      if (value >= 60 && value <= 100) return 'warning';
      return 'bad';
      
    default:
      return 'good';
  }
};

/**
 * School GPS data interface for map visualization
 */
export interface SchoolGPSData {
  school_id: number;
  school_name: string;
  school_reg_number: string;
  latitude: number;
  longitude: number;
  gps_source: string;
  gps_confidence: number;
  gps_matched_name?: string;
  region_name: string;
  district_name: string;
  school_type: string;
}

/**
 * Get schools GPS data for map markers
 */
export const getSchoolsGPSData = async (
  regionCode?: string,
  districtCode?: string
): Promise<SchoolGPSData[]> => {
  const params = new URLSearchParams();
  if (regionCode) params.append('region_code', regionCode);
  if (districtCode) params.append('district_code', districtCode);

  const url = `${API_BASE_URL}/schools-gps/${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch GPS data: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || [];
};

export default {
  getAnalysisData,
  getGeographicAnalysis,
  getTrendAnalysis,
  getComparativeAnalysis,
  getPredictiveInsights,
  getPerformanceIndicators,
  formatAnalysisNumber,
  calculateGrowthRate,
  getAnalysisTypeColor,
  getMetricStatus,
  getSchoolsGPSData,
};